/**
 * RFC 7807 (application/problem+json) error model for the cloneai API.
 *
 * See docs/api.md §2.2 for the canonical shape. `errorCode` is the stable
 * machine-readable discriminator; `title`/`detail` are human-readable.
 */
import { z } from 'zod';
/**
 * Zod schema for the RFC 7807 body the API emits.
 *
 * The spec uses `status` as required. `type`/`title`/`detail`/`instance`
 * are technically optional in RFC 7807; in cloneai responses they are
 * populated, but we tolerate absence to be permissive with edge-cases
 * (e.g. a 502 from an upstream before our handler runs).
 */
export const ProblemDocumentSchema = z
    .object({
    type: z.string().url().optional(),
    title: z.string().optional(),
    status: z.number().int().optional(),
    detail: z.string().optional(),
    instance: z.string().optional(),
    errorCode: z.string().optional(),
})
    .passthrough();
/**
 * Thrown by the api-client when the server returns a non-2xx response.
 *
 * Prefer calling `ApiError.is(err)` (or the code-specific `hasCode` helper)
 * over `instanceof` in dashboards/backoffice UIs — the latter can mis-fire
 * across bundler/worker realms.
 */
export class ApiError extends Error {
    name = 'ApiError';
    status;
    type;
    title;
    detail;
    instance;
    errorCode;
    extensions;
    requestId;
    constructor(init) {
        super(init.title ?? init.detail ?? `HTTP ${init.status}`);
        this.status = init.status;
        this.type = init.type;
        this.title = init.title;
        this.detail = init.detail;
        this.instance = init.instance;
        this.errorCode = init.errorCode;
        this.extensions = init.extensions ?? {};
        this.requestId = init.requestId;
    }
    /** Cross-realm-safe check for an ApiError. */
    static is(err) {
        return (typeof err === 'object' &&
            err !== null &&
            err.name === 'ApiError' &&
            typeof err.status === 'number');
    }
    /** Narrow by stable `errorCode`. */
    hasCode(code) {
        return this.errorCode === code;
    }
}
/**
 * Build an ApiError from a `fetch` Response whose body was already parsed
 * as JSON (or is unknown). Any shape that isn't valid RFC 7807 falls back
 * to a plain `{ status, title }` error so callers still get a useful throw.
 */
export function problemToApiError(status, body, opts = {}) {
    const parsed = ProblemDocumentSchema.safeParse(body);
    if (!parsed.success) {
        return new ApiError({
            status,
            title: `HTTP ${status}`,
            requestId: opts.requestId,
        });
    }
    const { type, title, status: bodyStatus, detail, instance, errorCode, ...rest } = parsed.data;
    return new ApiError({
        status: bodyStatus ?? status,
        type,
        title,
        detail,
        instance,
        errorCode,
        extensions: rest,
        requestId: opts.requestId,
    });
}
//# sourceMappingURL=errors.js.map